import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import {
    Cct,
    CctFee,
    FeeCountUnit,
    FeeType,
    MissionFee,
    Repetition,
    RepetitionType,
} from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import ActivableListItem from "../../ui_atoms/ActivableListItem";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import { useAtomValue } from "jotai";
import { currentClientMissionFeesRate } from "../../contexts/CreateMissionManager";
import FullWidthLine from "../FullWidthLine";
import Gap from "../../ui_atoms/Gap";
import ListItem from "../../ui_atoms/ListItem";
import { useTheme } from "../../hooks/use-theme";
import { v4 as uuidv4 } from "uuid";
import { useDeepCompareEffect } from "../../hooks/use-deep-compare-effect";
import { isEqual } from "lodash";
import { Platform, StyleSheet, TextInput } from "react-native";
import HStack from "../../ui_atoms/HStack";
import MoneyField from "../MoneyField";
import SelectField from "../SelectField";
import { formatCurrency } from "../../utils";
import { useSetAlert } from "../../hooks/use-set-alert";
import TextField from "../TextField";
import { MarkOptional } from "ts-essentials";
import { COLLAPSIBLE_VIEW_ANIMATION_DURATION } from "../../ui_atoms/CollapsibleView";
import { getCctFeeByType } from "../../hooks/cct-utils";

type FeeData = MarkOptional<
    Pick<
        MissionFee,
        "id" | "type" | "repetition" | "otherTitle" | "externalId" | "isAvsSalaryExempt"
    >,
    "id" | "type" | "repetition" | "isAvsSalaryExempt"
> & {
    amount?: number;
    countUnit?: FeeCountUnit | null;
    isEnabled: boolean;
};

type MissionFeeType = {
    feeType: FeeType;
    repetitionsOptions: RepetitionType[];
    defaultRepetition: RepetitionType;
};

export const missionFees: MissionFeeType[] = [
    {
        feeType: FeeType.PrivateTransport,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
    {
        feeType: FeeType.PublicTransport,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
    {
        feeType: FeeType.Food,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
];

export type MissionFeeResult = Partial<MissionFee>;

type MissionFeesProps = {
    setFees: (fees: Array<MissionFeeResult>) => void;
    fees: Array<MissionFee>;
    missionRepetition: Repetition;
    cct: Cct | null;
};

export const validateFeeData = (data: MissionFee) => {
    if (data.type === FeeType.Other && !data.otherTitle) {
        // if the fee is of type other, we need to validate that the title is not empty
        return false;
    }
    // if the fee is not of type other, and it is enabled, we need to validate that the
    // amount is greater than 0
    return (data.amount || 0) > 0;
};

const MissionFees: React.FC<MissionFeesProps> = ({ setFees, fees, missionRepetition, cct }) => {
    const i18n = useI18n();
    const clientMissionFeesRate = useAtomValue(currentClientMissionFeesRate);
    const [cachedFees, setCachedFees] = useState<MissionFeeResult[]>([]);

    const [isOtherFeesEnabled, setIsOtherFeesEnabled] = useState(
        fees.some((missionFee) => missionFee.type === FeeType.Other),
    );

    const byTypeValue = useMemo(() => {
        const defaultValues = missionFees.reduce(
            (result, missionFee) => {
                const cctFee = getCctFeeByType(cct, missionFee.feeType);
                if (cctFee) {
                    return {
                        ...result,
                        [missionFee.feeType]: {
                            type: missionFee.feeType,
                            isEnabled: false,
                            repetition:
                                (cctFee.repetition as RepetitionType) ??
                                missionFee.defaultRepetition,
                            isAvsSalaryExempt: cctFee.isAvsSalaryExempt,
                            amount: cctFee.amount != null ? cctFee.amount / 100 : undefined,
                            countUnit: (cctFee.countUnit as FeeCountUnit) ?? FeeCountUnit.Default,
                        },
                    };
                }
                return {
                    ...result,
                    [missionFee.feeType]: {
                        type: missionFee.feeType,
                        isEnabled: false,
                        repetition: undefined,
                    },
                };
            },
            {} as Record<FeeType, FeeData>,
        );
        for (const fee of fees || []) {
            if (fee && fee.type && fee.type !== FeeType.Cct) {
                defaultValues[fee.type] = {
                    type: fee.type,
                    amount: fee.amount !== undefined ? fee.amount / 100 : undefined,
                    repetition: fee.repetition || RepetitionType.WorkedDay,
                    otherTitle:
                        fee.type === FeeType.Other
                            ? fee.otherTitle || ""
                            : i18n.t(`fees.type.${fee.type}.title`),
                    externalId: fee.externalId || null,
                    isEnabled: true,
                    isAvsSalaryExempt: fee.isAvsSalaryExempt,
                    countUnit: fee.countUnit ?? FeeCountUnit.Default,
                };
            }
        }
        return defaultValues;
    }, [fees, i18n, cct]);

    // AVS-exempt fees carry no social charges, so their invoice amount equals the raw amount.
    const computeInvoiceAmount = useCallback(
        (amount?: number, isAvsSalaryExempt?: boolean | null) => {
            if (!amount) {
                return undefined;
            }
            return isAvsSalaryExempt
                ? Math.round(amount * 100)
                : Math.trunc(amount * (100 + Number(clientMissionFeesRate)));
        },
        [clientMissionFeesRate],
    );

    const onChangeFee = useCallback(
        (feeData: Partial<FeeData>) => {
            let feesResult = missionFees.reduce<Array<MissionFeeResult>>(
                (result, missionFee) => {
                    if (missionFee.feeType === feeData.type) {
                        if (feeData.isEnabled) {
                            result.push({
                                type: missionFee.feeType,
                                amount: feeData.amount
                                    ? Math.round((feeData.amount || 0) * 100)
                                    : undefined,
                                invoiceAmount: computeInvoiceAmount(
                                    feeData.amount,
                                    feeData.isAvsSalaryExempt,
                                ),
                                repetition: feeData.repetition,
                                otherTitle: feeData.otherTitle || undefined,
                                externalId: feeData.externalId || undefined,
                                isAvsSalaryExempt: feeData.isAvsSalaryExempt || undefined,
                                countUnit: feeData.countUnit ?? FeeCountUnit.Default,
                            });
                        }
                    } else {
                        const currentValue = byTypeValue[missionFee.feeType];
                        if (currentValue.isEnabled) {
                            result.push({
                                type: currentValue.type,
                                amount: Math.round((currentValue.amount || 0) * 100),
                                invoiceAmount: computeInvoiceAmount(
                                    currentValue.amount,
                                    currentValue.isAvsSalaryExempt,
                                ),
                                repetition: currentValue.repetition,
                                otherTitle: currentValue.otherTitle || undefined,
                                externalId: currentValue.externalId || undefined,
                                isAvsSalaryExempt: currentValue.isAvsSalaryExempt || undefined,
                                countUnit: currentValue.countUnit ?? FeeCountUnit.Default,
                            });
                        }
                    }
                    return result;
                },
                fees.filter((f) => f.type === FeeType.Other),
            );

            if (feeData.type === FeeType.Other) {
                const currentOtherFeeIndex = feesResult.findIndex((f) => f.id === feeData.id);

                if (currentOtherFeeIndex !== -1) {
                    if (feeData.isEnabled) {
                        const { isEnabled: _, ...restFeeData } = feeData;
                        feesResult[currentOtherFeeIndex] = {
                            ...cachedFees[currentOtherFeeIndex],
                            ...restFeeData,
                            amount: feeData.amount
                                ? Math.round((feeData.amount || 0) * 100)
                                : undefined,
                            invoiceAmount: computeInvoiceAmount(
                                feeData.amount,
                                feeData.isAvsSalaryExempt,
                            ),
                        };
                    } else {
                        feesResult = feesResult.filter((f) => f.id !== feeData.id);
                    }
                } else {
                    if (feeData.isEnabled) {
                        const { isEnabled: _isEnabled, ...restFeeData } = feeData;
                        feesResult.push({
                            ...restFeeData,
                            amount: feeData.amount
                                ? Math.round((feeData.amount || 0) * 100)
                                : undefined,
                            invoiceAmount: computeInvoiceAmount(
                                feeData.amount,
                                feeData.isAvsSalaryExempt,
                            ),
                        });
                    }
                }
            }

            const newCachedFees = [
                ...feesResult.filter((fee) => fee.type !== FeeType.Other),
                ...cachedFees.filter((fee) => fee.type === FeeType.Other),
            ];

            setCachedFees(newCachedFees);

            const _isOtherFeesEnabled = feesResult.some(
                (missionFee) => missionFee.type === FeeType.Other,
            );

            setIsOtherFeesEnabled(_isOtherFeesEnabled);

            const filteredFees = feesResult.filter(
                (fee) => _isOtherFeesEnabled || fee.type !== FeeType.Other,
            );

            setFees(filteredFees);
        },
        [byTypeValue, cachedFees, computeInvoiceAmount, fees, setFees],
    );

    useDeepCompareEffect(() => {
        if (!isEqual(cachedFees, fees)) {
            setIsOtherFeesEnabled(fees.some((missionFee) => missionFee.type === FeeType.Other));
        }
    }, [fees]);

    const buildRepetitionOption = useCallback(
        (repetitionOption: RepetitionType, countUnit?: FeeCountUnit | null) => {
            let label = i18n.t(`fees.repetition.${repetitionOption}`);
            if (
                missionRepetition === Repetition.None &&
                repetitionOption === RepetitionType.WorkedMonth
            ) {
                label = i18n.t("fees.repetition.total");
            }
            if (
                repetitionOption === RepetitionType.Count &&
                countUnit &&
                countUnit !== FeeCountUnit.Default
            ) {
                // A non-default count unit (e.g. km) overrides the generic "per occurrence" label.
                label = i18n.t(`fees.repetition.count_${countUnit}`);
            }
            return {
                label,
                value: repetitionOption,
            };
        },
        [i18n, missionRepetition],
    );

    const otherFees = useMemo(
        () => fees.filter((missionFee) => missionFee.type === FeeType.Other),
        [fees],
    );

    const renderFee = useCallback(
        (missionFee: MissionFeeType) => {
            const currentFee = fees.find((f) => f.type === missionFee.feeType);
            const cctFee = getCctFeeByType(cct, missionFee.feeType);

            return (
                <React.Fragment key={missionFee.feeType}>
                    <HStack h="2/3" style={{ width: "100%" }}>
                        <ActivableMissionFee
                            title={i18n.t(`fees.type.${missionFee.feeType}.title`)}
                            description={i18n.t(`fees.type.${missionFee.feeType}.description`)}
                            repetitionsOptions={missionFee.repetitionsOptions.map((option) =>
                                buildRepetitionOption(
                                    option,
                                    byTypeValue[missionFee.feeType].countUnit,
                                ),
                            )}
                            isEnabled={byTypeValue[missionFee.feeType].isEnabled}
                            value={byTypeValue[missionFee.feeType]}
                            onChange={(changes) => {
                                onChangeFee({
                                    ...currentFee,
                                    ...byTypeValue[missionFee.feeType],
                                    ...changes,
                                });
                            }}
                            cctFee={cctFee}
                        />
                    </HStack>
                    <Gap gap="2/3" />
                    <FullWidthLine />
                </React.Fragment>
            );
        },
        [buildRepetitionOption, byTypeValue, fees, i18n, onChangeFee, cct],
    );

    return (
        <VStack gap={1}>
            <BodyMediumText>
                {i18n.t("screens.user.create_mission_fees.description")}
            </BodyMediumText>
            <>
                <VStack gap="2/3">
                    <VStack gap="2/3">{missionFees.map(renderFee)}</VStack>
                    <HStack h="2/3">
                        <ActivableMissionFee
                            title={i18n.t(`fees.type.other.title`)}
                            repetitionsOptions={[
                                RepetitionType.WorkedMonth,
                                RepetitionType.WorkedDay,
                                RepetitionType.Count,
                            ].map((option) => buildRepetitionOption(option))}
                            isEnabled={isOtherFeesEnabled}
                            value={{ type: FeeType.Other, otherTitle: null, externalId: null }}
                            otherFees={otherFees}
                            onChange={(changes) => {
                                const isAddingOrRemoving = changes.isEnabled !== undefined;

                                if (isAddingOrRemoving) {
                                    const isDeleteFee =
                                        changes.isEnabled === false &&
                                        // if id exists, we only want to delete a specific fee
                                        !!changes.id &&
                                        // if we try to delete the last remaining fee, we suppose to uncheck the fee entirely
                                        otherFees.length > 1;

                                    const baseOtherFeeConfig = {
                                        isEnabled: true,
                                        ...changes,
                                    };

                                    if (baseOtherFeeConfig.isEnabled && !isOtherFeesEnabled) {
                                        if (
                                            cachedFees.filter((fee) => fee.type === FeeType.Other)
                                                .length === 0
                                        ) {
                                            // Add a first other fee when the Switch is turned on the first time
                                            onChangeFee({
                                                id: uuidv4(),
                                                isEnabled: true,
                                                type: FeeType.Other,
                                                repetition: RepetitionType.WorkedDay,
                                            });
                                        } else {
                                            setFees(cachedFees);
                                        }
                                        setIsOtherFeesEnabled(true);
                                    } else if (!baseOtherFeeConfig.isEnabled && !isDeleteFee) {
                                        // When disabling Other fees, save current `fees` in `cachedFees`
                                        setCachedFees(fees);
                                        setFees(fees.filter((fee) => fee.type !== FeeType.Other));
                                    } else {
                                        onChangeFee(baseOtherFeeConfig);
                                    }
                                } else {
                                    onChangeFee({ isEnabled: true, ...changes });
                                }
                            }}
                        />
                    </HStack>
                    {/* Necessary to have correct padding at the bottom when other fees is closed */}
                    {!isOtherFeesEnabled && <></>}
                </VStack>
            </>
        </VStack>
    );
};

type RepetitionOptions = {
    label: string;
    value: RepetitionType;
}[];

type OtherFeeItemProps = {
    fee: MissionFee;
    index: number;
    repetitionsOptions: RepetitionOptions;
    onChange: (value: Partial<FeeData>) => void;
};

const OtherFeeItem = ({ fee, index, repetitionsOptions, onChange }: OtherFeeItemProps) => {
    const i18n = useI18n();
    const { colors, marginSize } = useTheme();
    const { setAlert } = useSetAlert();
    const moneyLineRef = useRef<MoneyLineRefs>(null);
    const [isOtherTitleFocused, setIsOtherTitleFocused] = useState(false);
    const clientMissionFeesRate = useAtomValue(currentClientMissionFeesRate);

    const otherFeeConfig = useMemo(
        () => ({
            ...fee,
            type: FeeType.Other,
            amount: fee.amount !== undefined ? fee.amount / 100 : undefined,
            repetition: fee.repetition || RepetitionType.WorkedDay,
        }),
        [fee],
    );

    return (
        <VStack h="2/3">
            {index > 0 && <Gap gap="2/3" />}
            <TextField
                autoFocus={Platform.OS === "web" && index === 0}
                style={{ marginBottom: marginSize / 2 }}
                value={fee.otherTitle || ""}
                onChangeText={(newValue) => {
                    onChange({ ...otherFeeConfig, otherTitle: newValue });
                }}
                maxLength={100}
                label={i18n.t(`screens.user.create_mission_fees.fees_type`)}
                showErrorText={false}
                returnKeyType="next"
                error={!isOtherTitleFocused && !fee.otherTitle}
                onFocus={() => setIsOtherTitleFocused(true)}
                onBlur={() => setIsOtherTitleFocused(false)}
                onSubmitEditing={() => {
                    moneyLineRef.current?.focusMoneyField();
                }}
            />
            <MoneyLine
                ref={moneyLineRef}
                fee={otherFeeConfig}
                repetitionsOptions={repetitionsOptions}
                onChangeCb={(changes) => {
                    onChange({ ...otherFeeConfig, ...changes });
                }}
            />
            <Gap gap="1/2" />
            <BodySmallText>
                {i18n.t("screens.user.create_mission_fees.total_with_charge", {
                    amount: otherFeeConfig.amount
                        ? formatCurrency({
                              amountInCents: Math.round(
                                  (otherFeeConfig.amount || 0) *
                                      (100 + Number(clientMissionFeesRate)),
                              ),
                          })
                        : "--",
                })}
            </BodySmallText>
            <ListItem
                description={i18n.t("screens.user.create_mission_fees.delete_other_fees")}
                rightIcon="trash"
                rightActionMode="medium"
                style={{ marginHorizontal: -marginSize }}
                rightIconColor={colors.error}
                globalTextColor={colors.error}
                onPress={() => {
                    setAlert({
                        title: i18n.t("screens.user.create_mission_fees.remove_title"),
                        message: i18n.t("screens.user.create_mission_fees.remove_info"),
                        buttons: [
                            {
                                text: i18n.t("cancel"),
                                style: "cancel",
                            },
                            {
                                text: i18n.t("screens.user.create_mission_fees.do_remove"),
                                style: "destructive",
                                onPress: () => {
                                    onChange({ ...otherFeeConfig, isEnabled: false });
                                },
                            },
                        ],
                        options: { cancelable: true },
                    });
                }}
            />
        </VStack>
    );
};

type ActivableMissionFeeProps = {
    title: string;
    onChange: (value: Partial<FeeData>) => void;
    repetitionsOptions: RepetitionOptions;
    isEnabled?: boolean;
    value: Omit<FeeData, "isEnabled">;
    description?: string;
    otherFees?: MissionFee[];
    cctFee?: CctFee;
};

const ActivableMissionFee = ({
    title,
    description,
    repetitionsOptions,
    isEnabled = false,
    onChange,
    value,
    otherFees,
    cctFee,
}: ActivableMissionFeeProps) => {
    const i18n = useI18n();
    const { colors, marginSize } = useTheme();
    const isOther = useMemo(() => value.type === FeeType.Other, [value]);
    const client_mission_fees_rate = useAtomValue(currentClientMissionFeesRate);

    // The CCT fixes a value only for the fields it actually provides, so lock per-field.
    const lockAmount = cctFee?.amount != null;
    const lockRepetition = cctFee?.repetition != null;

    const moneyLineRef = useRef<MoneyLineRefs>(null);

    return (
        <ActivableListItem
            title={title}
            enabled={isEnabled}
            style={{ marginHorizontal: -marginSize, flex: 1 }}
            collapsibleViewStyle={isOther ? { marginHorizontal: -marginSize } : undefined}
            onSwitch={(newValue) => {
                onChange({ isEnabled: newValue });
                if (!isOther && newValue) {
                    setTimeout(() => {
                        moneyLineRef.current?.focusMoneyField();
                    }, COLLAPSIBLE_VIEW_ANIMATION_DURATION);
                }
            }}
        >
            {isOther ? (
                <>
                    {otherFees?.map((fee, index) => (
                        <OtherFeeItem
                            key={fee.id}
                            fee={fee}
                            index={index}
                            repetitionsOptions={repetitionsOptions}
                            onChange={onChange}
                        />
                    ))}
                    <ListItem
                        labels={[
                            {
                                text: i18n.t(`screens.user.create_mission_fees.add_other_fees`),
                                mode: "cta",
                            },
                        ]}
                        rightIcon="add"
                        rightActionMode="medium"
                        rightIconColor={colors.secondary}
                        style={{ borderBottomWidth: 0 }}
                        onPress={() => {
                            onChange({
                                id: uuidv4(),
                                isEnabled: true,
                                type: FeeType.Other,
                                repetition: RepetitionType.WorkedDay,
                            });
                        }}
                    />
                </>
            ) : (
                <>
                    <MoneyLine
                        fee={value}
                        onChangeCb={onChange}
                        repetitionsOptions={repetitionsOptions}
                        ref={moneyLineRef}
                        disableAmount={lockAmount}
                        disableRepetition={lockRepetition}
                    />
                    <Gap gap="1/2" />
                    {(lockAmount || lockRepetition) && (
                        <BodySmallText>
                            {i18n.t("screens.user.create_mission_fees.cct_fixed_fee")}
                        </BodySmallText>
                    )}
                    {!value.isAvsSalaryExempt && (
                        <BodySmallText>
                            {i18n.t("screens.user.create_mission_fees.total_with_charge", {
                                amount: value.amount
                                    ? formatCurrency({
                                          amountInCents: Math.round(
                                              (value.amount || 0) *
                                                  (100 + Number(client_mission_fees_rate)),
                                          ),
                                      })
                                    : "--",
                            })}
                        </BodySmallText>
                    )}
                </>
            )}
            {description && <BodySmallText color={colors.fieldLabel}>{description}</BodySmallText>}
        </ActivableListItem>
    );
};

type MoneyLineProps = {
    onChangeCb: (onChangeCb: Partial<FeeData>) => void;
    fee: Omit<FeeData, "isEnabled">;
    repetitionsOptions: { label: string; value: RepetitionType }[];
    disableAmount?: boolean;
    disableRepetition?: boolean;
};

type MoneyLineRefs = {
    focusMoneyField: () => void;
};

const MoneyLine = ({
    onChangeCb,
    fee,
    repetitionsOptions,
    disableAmount,
    disableRepetition,
    ref,
}: MoneyLineProps & { ref?: React.Ref<MoneyLineRefs> }) => {
    const styles = useStyles();

    const valueInputRef = useRef<TextInput>(null);

    const [isTouched, setIsTouched] = useState(false);
    const [isValueFocused, setIsValueFocused] = useState(false);

    const hasSingleRepetitionOption = useMemo(
        () => repetitionsOptions.length === 1 && repetitionsOptions[0].value == fee.repetition,
        [fee.repetition, repetitionsOptions],
    );

    useImperativeHandle(ref, () => ({
        focusMoneyField: () => {
            valueInputRef.current?.focus();
        },
    }));

    return (
        <HStack gap="1/2" wrapChildren={false}>
            <MoneyField
                ref={valueInputRef}
                error={!isValueFocused && !fee.amount && isTouched}
                showErrorText={false}
                maxValue={9999}
                selectTextOnFocus
                style={styles.amountField}
                value={fee.amount}
                onFocus={() => {
                    setIsTouched(true);
                    setIsValueFocused(true);
                }}
                onBlur={() => {
                    setIsValueFocused(false);
                }}
                textAlign="right"
                onChange={(newAmount) => onChangeCb({ amount: newAmount })}
                disabled={disableAmount}
            />
            {hasSingleRepetitionOption ? (
                <BodyMediumText>{repetitionsOptions[0].label}</BodyMediumText>
            ) : (
                <SelectField
                    style={styles.repetitionField}
                    value={fee.repetition}
                    data={repetitionsOptions}
                    onSelect={(newRepetition) => {
                        onChangeCb({
                            repetition: newRepetition.value,
                        });
                    }}
                    disabled={disableRepetition}
                />
            )}
        </HStack>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                amountField: {
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 135,
                },
                repetitionField: {
                    flexGrow: 1,
                    width: 100, // need to set a width for layout to work. but flexGrow will extend it to fill the space
                },
            }),
        [],
    );
};

export default MissionFees;
